import type { HTMLAttributes, PropsWithChildren } from "react";

import {
  TTypographyColor,
  TTypographyTag,
} from "@/ui/components/typography/typography.config";
import { DefinedDefaults, SubsetKeys, subsetOf } from "@/ui/lib/types";
import { TTextTitleProps } from "@/ui/components/typography/TextTitle";
import { TTextSubtitleProps } from "@/ui/components/typography/TextSubtitle";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const AVAILABLE_TEXT_EYEBROW_TAGS = subsetOf<TTypographyTag>()([ "h1", "h2", "h3", "h4", "h5" ]);
export const AVAILABLE_TEXT_EYEBROW_CHILD_TAGS = subsetOf<TTypographyTag>()([ "h2", "h3", "h4", "h5", "h6" ]);

export const AVAILABLE_TEXT_EYEBROW_COLORS =
  subsetOf<TTypographyColor>()([ "default", "secondary" ]);

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TTextEyebrowTag = SubsetKeys<typeof AVAILABLE_TEXT_EYEBROW_TAGS>;
export type TTextEyebrowChildTag = SubsetKeys<typeof AVAILABLE_TEXT_EYEBROW_CHILD_TAGS>;
export type TTextEyebrowColor = SubsetKeys<typeof AVAILABLE_TEXT_EYEBROW_COLORS>;

export type TTextEyebrowProps = {
  color?: TTextEyebrowColor;
  tag?: TTextEyebrowTag;
  child: TTextEyebrowChild;
} & PropsWithChildren & HTMLAttributes<HTMLElement>;

type TTextEyebrowDefaultProps = DefinedDefaults<
  TTextEyebrowProps,
  "color" | "tag"
>;

type TextEyebrowChildComponent = "title" | "subtitle";

interface ITextEyebrowBaseChild {
  component: TextEyebrowChildComponent;
}

export interface ITextEyebrowChildTitle extends ITextEyebrowBaseChild, Omit<TTextTitleProps, "tag" | "align"> {
  component: "title"
}

export interface ITextEyebrowChildSubtitle extends ITextEyebrowBaseChild, Omit<TTextSubtitleProps, "tag" | "align"> {
  component: "subtitle"
}

export type TTextEyebrowChild = ITextEyebrowChildTitle | ITextEyebrowChildSubtitle;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const TEXT_EYEBROW_DEFAULTS = {
  color: "default",
  tag: "h2",
} satisfies TTextEyebrowDefaultProps;
